/**
 * `fn.tools.hybridXOffset` (fn_tools.js:1195-1267 on KA-x-user-skill) — the
 * shared X-Offset convention the Add Section dialog's "+ / −" column is built
 * on. Ported here rather than reused from `canvas3dGeometry`'s copy because
 * that one is a method on the canvas geometry class and takes a
 * `CanvasTunnelModel`, not the plain `IAhuBoardTunnel` the dialog has.
 *
 * The convention, unchanged from legacy: `x_offset` IS the tunnel's own
 * UPSTREAM edge's absolute X — the left edge for Left-to-Right, the right
 * edge for Right-to-Left. There is deliberately no sign flip between the two
 * directions (KA-x-axis-sign).
 */

export type XOffsetSign = 'plus' | 'minus';

/**
 * The three fields the span is built from. Deliberately structural rather than
 * `IAhuBoardTunnel`: the canvas carries its own plain row type (`CanvasTunnel`)
 * and flips airflow direction from there, and legacy read the same three
 * attributes off the one Backbone model both layers shared.
 */
export interface TunnelSpanFields {
  length?: number | string | null;
  x_offset?: number | string | null;
  airflow_direction_id?: number | string | null;
}

/**
 * `getTunnelPhysicalSpan` — {left, right} physical X span (inches) of an
 * EXISTING tunnel, read through its OWN airflow direction. Peers in one
 * row:level chain are not guaranteed to share a direction (a user can flip an
 * individual tunnel), so this must be computed per tunnel — sorting on raw
 * `x_offset` instead silently breaks as soon as a chain mixes directions.
 */
export function getTunnelPhysicalSpan(tunnel: TunnelSpanFields): {
  left: number;
  right: number;
} {
  const length = parseFloat(String(tunnel.length ?? 0)) || 0;
  const xOffset = parseFloat(String(tunnel.x_offset ?? 0)) || 0;
  const isRtl = Number(tunnel.airflow_direction_id) === 2;
  if (isRtl) return { left: xOffset - length, right: xOffset };
  return { left: xOffset, right: xOffset + length };
}

/**
 * `TunnelSpanFields` plus the two address fields needed to find which chain a
 * tunnel belongs to. `position` is deliberately absent: a chain's footprint is
 * the union of its members' spans, whatever order they are numbered in.
 */
export interface TunnelChainFields extends TunnelSpanFields {
  row?: number | null;
  level?: number | null;
}

/**
 * The physical X footprint of one whole `row:level` chain — the union of its
 * tunnels' spans, each read through its OWN airflow direction, so a chain that
 * mixes directions still resolves.
 *
 * Tunnels with no length are ignored: a tunnel holding no sections has no
 * footprint to align against, and one sitting at the model default `x_offset`
 * of 0 would otherwise drag the chain's left edge to 0. `null` when the chain
 * has no tunnel with a footprint.
 */
export function getChainPhysicalSpan(
  tunnels: TunnelChainFields[],
  row: number,
  level: number
): { left: number; right: number } | null {
  const spans = tunnels
    .filter(
      (t) =>
        (t.row ?? 1) === row &&
        (t.level ?? 1) === level &&
        (parseFloat(String(t.length ?? 0)) || 0) > 0
    )
    .map(getTunnelPhysicalSpan);
  if (!spans.length) return null;
  return {
    left: Math.min(...spans.map((s) => s.left)),
    right: Math.max(...spans.map((s) => s.right)),
  };
}

/**
 * `alignmentSpanForNewChain` — the footprint a BRAND-NEW `row:level` chain
 * starts flush with: the chain it is physically carried by.
 *
 * Why it exists: a new chain has no peer to butt up against, and defaulting its
 * X-Offset to 0 placed it at the AHU's absolute origin — which is only where
 * the unit begins when `1:1`'s own chain happens to start at 0. On a unit whose
 * levels sit at 78–150 (AHU 3972), the first tunnel of a new level 3 came out at
 * 0–24, detached from the stack it is supposed to sit on, and the user had to
 * work the real number out by hand. A new level starts where the level below it
 * starts (reported 2026-10-07).
 *
 * Which chain, in order of preference — the same neighbour rule the create path
 * already uses for the `index_id`/width/height it inherits
 * (`hybridInsertionBindings.peerAt`) and the Y axis for `tunnel_offset`
 * (`hybridYOffset.findYPeer`): the level below in this row, descending to level
 * 1; then this level in the row to the left, descending to row 1; then the
 * `1:1` anchor. `null` when nothing on the AHU has a footprint yet, which
 * leaves the X = 0 start of the very first chain alone.
 */
export function alignmentSpanForNewChain(
  target: { row: number; level: number },
  tunnels: TunnelChainFields[]
): { left: number; right: number } | null {
  const candidates: Array<[number, number]> = [];
  for (let l = target.level - 1; l >= 1; l -= 1) candidates.push([target.row, l]);
  for (let r = target.row - 1; r >= 1; r -= 1) candidates.push([r, target.level]);
  candidates.push([1, 1]);
  for (const [r, l] of candidates) {
    const span = getChainPhysicalSpan(tunnels, r, l);
    if (span) return span;
  }
  return null;
}

/**
 * `defaultOffsetForNewTunnel` — signed `x_offset` for a NEW tunnel of
 * `length` / `airflowDirectionId` whose physical span butts up against
 * `extensionPoint`. 'plus' extends the new span rightward from that point,
 * 'minus' leftward. Returns the new tunnel's upstream edge directly — a signed
 * absolute X, which for a non-zero `extensionPoint` can perfectly well be
 * positive under 'minus' (extend leftward from 78 and you are still at 54).
 * `applySign` is what carries that sign back out to the input's magnitude.
 */
export function defaultOffsetForNewTunnel(
  length: number,
  airflowDirectionId: number,
  extensionPoint: number,
  sign: XOffsetSign
): number {
  const len = Number(length) || 0;
  let targetLeft: number;
  let targetRight: number;
  if (sign === 'plus') {
    targetLeft = extensionPoint;
    targetRight = targetLeft + len;
  } else {
    targetRight = extensionPoint;
    targetLeft = targetRight - len;
  }
  return Number(airflowDirectionId) === 2 ? targetRight : targetLeft;
}

/**
 * The inverse of `getTunnelPhysicalSpan`: the `x_offset` a tunnel needs to
 * occupy `span` physically when it runs in `airflowDirectionId` — the span's
 * upstream edge, which is its LEFT for Left-to-Right and its RIGHT for
 * Right-to-Left.
 *
 * What it is for: the two tunnels of a dual-tunnel component (Heat Wheel /
 * Plate Heatex) must cover the SAME physical X span — the component spans both
 * of them — and the Add Section dialog offers one X-Offset input for the pair.
 * When the pair runs counter-flow (which a Heat Wheel always does) the two
 * tunnels name opposite edges of that one span, so the same stored number
 * cannot serve both: sending it to both put the second tunnel a full section
 * length away from the first. Resolve the span once, then ask this for each
 * tunnel's own number.
 */
export function offsetForPhysicalSpan(
  span: { left: number; right: number },
  airflowDirectionId: number
): number {
  return Number(airflowDirectionId) === 2 ? span.right : span.left;
}

/**
 * `applySign` — the signed `x_offset` behind the UNSIGNED magnitude the
 * X-Offset input holds. The row's own computed default (`defaultOffset`) is
 * what supplies the sign, so a typed magnitude can never disagree with the
 * geometry the "+ / −" toggle asked for; the toggle itself only decides the
 * sign when the default is 0 and has none of its own.
 *
 * Why not the toggle alone (`sign === 'minus' ? -m : m`, which this replaces):
 * the toggle is a direction to EXTEND in, and `x_offset` is an absolute
 * upstream-edge X — the two coincide only while the alignment point is 0.
 * Extend leftward from a chain that begins at 78 and the answer is +54, which
 * the toggle-only rule stored as −54, i.e. 132" upstream of where the dialog
 * had just shown the section going. Unreachable in legacy, where every default
 * was anchored at 0; reachable here as soon as an alignment point is not
 * (`alignmentSpanForNewChain`), and already reachable before that via "−" on a
 * chain that does not start at 0.
 */
export function applySign(
  magnitude: number,
  defaultOffset: number,
  sign: XOffsetSign
): number {
  const m = Math.abs(Number(magnitude) || 0);
  const negative = defaultOffset < 0 || (defaultOffset === 0 && sign === 'minus');
  return negative ? -m : m;
}

/**
 * `model/tunnel.js toggle_airflow_direction` (:203-230 on KA-x-user-skill) —
 * the PATCH body for flipping ONE tunnel's airflow direction.
 *
 * `airflow_direction_id` alone is not enough on a hybrid AHU. `x_offset` is the
 * tunnel's UPSTREAM edge's absolute X — the LEFT edge for Left-to-Right, the
 * RIGHT edge for Right-to-Left, with no sign flip between the two
 * (KA-x-axis-sign, see `getTunnelPhysicalSpan`). So flipping the direction
 * changes WHICH edge `x_offset` names, and leaving the stored number alone
 * slides the tunnel by its own length: a 24"-long section at `x_offset: 24`
 * spans 24→48 as LTR and 0→24 as RTL, i.e. it lands on top of its upstream
 * neighbour. Legacy therefore reads the CURRENT physical span first and hands
 * the new direction whichever edge it now names.
 *
 * Non-hybrid AHUs treat `x_offset` as a raw physical nudge that airflow does
 * not affect, so `x_offset` is left out of the body entirely there.
 */
export function airflowFlipPatch(
  tunnel: TunnelSpanFields,
  isHybrid: boolean
): { airflow_direction_id: number; x_offset?: number } {
  // tunnel_airflow_directions has exactly the two CMS rows (ids 1/2, G36 unit
  // convention) — legacy picks "the one that isn't current".
  const next = Number(tunnel.airflow_direction_id) === 1 ? 2 : 1;
  if (!isHybrid) return { airflow_direction_id: next };
  const currentlyLtr = Number(tunnel.airflow_direction_id) !== 2;
  const span = getTunnelPhysicalSpan(tunnel);
  return { airflow_direction_id: next, x_offset: currentlyLtr ? span.right : span.left };
}
